/**
 * @page PreiskalkulatorPublic
 * @route /calculator (public) and /calculator/internal (auth)
 * @description Öffentlicher Preiskalkulator — Standalone-Layout ohne Sidebar/TopBar.
 *   Public: Lead-Formular + "Angebot anfordern" CTA.
 *   Internal: "Lead erstellen" Button (direkt in Pipeline, mit Auth).
 */
import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Calculator, FileDown, ExternalLink, Send, CheckCircle, Plus } from 'lucide-react';
import CalculatorCore, { formatPrice } from '../components/CalculatorCore';

const API = import.meta.env.VITE_API_URL || '';

export default function PreiskalkulatorPublic({ internal = false }) {
  const { t, language, setLanguage } = useLanguage();

  // Default-Sprache für Public-Kalkulator: DE (DACH-Zielgruppe)
  useEffect(() => {
    const stored = localStorage.getItem('weship-language');
    if (!stored) setLanguage('de');
  }, [setLanguage]);

  // Lead form state (public mode)
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const formRef = useRef(null);

  // Internal mode: lead creation state
  const [leadCreating, setLeadCreating] = useState(false);
  const [leadCreated, setLeadCreated] = useState(false);

  // Store latest result for lead submission
  const latestRef = useRef({ result: null, selections: null });

  // #1512 — Attribution beim Seitenaufruf aus der URL fangen (Query überlebt nur im POST-Body)
  const utmRef = useRef({});
  useEffect(() => {
    if (internal) return; // interne Kalkulation trägt keine Kampagnen-Attribution
    try {
      const params = new URLSearchParams(window.location.search);
      const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
      const captured = {};
      for (const k of keys) {
        const v = params.get(k);
        if (v) captured[k] = v;
      }
      utmRef.current = captured;
    } catch { /* no-op */ }
  }, [internal]);

  const handleResultChange = (result, selections) => {
    latestRef.current = { result, selections };
  };

  // ── Public: Submit lead form ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      setFormError(t('calcPublic.requiredFields') || 'Please fill in all required fields.');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      const { result, selections } = latestRef.current;
      const monthlyPkgs = selections?.tier
        ? Math.round((selections.tier.min_packages + selections.tier.max_packages) / 2) : 0;
      const res = await fetch(`${API}/api/public/calculator-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: form.company,
          name: form.name,
          email: form.email,
          phone: form.phone,
          ...utmRef.current,
          calcData: {
            monthlyPackages: monthlyPkgs,
            totalPerPackage: result?.totalPerPackage || 0,
            warehouse: selections?.warehouse?.name,
            country: selections?.country?.name,
            carrier: selections?.carrier?.name,
            tier: selections?.tier?.name,
            weight: selections?.weight,
            skus: selections?.skus,
            picks: selections?.picks,
          },
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setForm({ company: '', name: '', email: '', phone: '' });
      } else {
        setFormError('Error submitting. Please try again.');
      }
    } catch {
      setFormError('Error submitting. Please try again.');
    }
    setSubmitting(false);
  };

  // ── Internal: Create lead directly ──
  const handleCreateLead = async () => {
    setLeadCreating(true);
    try {
      const { result, selections } = latestRef.current;
      const monthlyPkgs = selections?.tier
        ? Math.round((selections.tier.min_packages + selections.tier.max_packages) / 2) : 0;
      const res = await fetch(`${API}/api/public/calculator-lead`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company: 'Interne Kalkulation',
          name: 'Intern',
          email: 'intern@weship.eu',
          calcData: {
            monthlyPackages: monthlyPkgs,
            totalPerPackage: result?.totalPerPackage || 0,
            warehouse: selections?.warehouse?.name,
            country: selections?.country?.name,
            carrier: selections?.carrier?.name,
            tier: selections?.tier?.name,
            weight: selections?.weight,
            skus: selections?.skus,
            picks: selections?.picks,
          },
        }),
      });
      if (res.ok) {
        setLeadCreated(true);
        setTimeout(() => setLeadCreated(false), 3000);
      }
    } catch { /* ignore */ }
    setLeadCreating(false);
  };

  // ── Custom action buttons for result panel ──
  const renderActions = ({ result, handlePdfExport }) => (
    <div className="mt-5 space-y-2">
      <button
        onClick={handlePdfExport}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[8px] border border-[var(--border-card)] text-[13px] font-medium text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
      >
        <FileDown className="w-4 h-4" />
        {t('calculator.savePdf')}
      </button>
      {internal ? (
        <button
          onClick={handleCreateLead}
          disabled={leadCreating || !result}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[8px] bg-[var(--primary)] text-white text-[13px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {leadCreated ? (
            <><CheckCircle className="w-4 h-4" /> {t('calcPublic.leadCreated')}</>
          ) : (
            <><Plus className="w-4 h-4" /> {t('calcPublic.createLead')}</>
          )}
        </button>
      ) : (
        <button
          onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}
          disabled={!result}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-[8px] bg-[var(--primary)] text-white text-[13px] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
          {t('calcPublic.requestOffer')}
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F5FA] flex flex-col" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* ── Header ── */}
      <header className="bg-white border-b border-[var(--border-default,#E5E7EB)]">
        <div className="max-w-[1360px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/weship-logo.png" alt="WeShip" className="h-9 w-auto" />
            <div>
              <div className="text-[11px] text-[#6B7280]">{t('calcPublic.title')}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* DE/EN Toggle */}
            <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] flex">
              {['DE', 'EN'].map(lang => (
                <button
                  key={lang}
                  onClick={() => setLanguage(lang.toLowerCase())}
                  className={`px-3 py-1 rounded-[8px] text-[12px] font-semibold transition-all ${
                    language === lang.toLowerCase()
                      ? 'bg-white shadow-sm text-[#1A1A2E]'
                      : 'text-[#6B7280] hover:text-[#1A1A2E]'
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>
            {!internal && (
              <a
                href="https://www.weship.eu/kontakt/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#338edc] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
              >
                {t('calculator.contactExpert')}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 max-w-[1360px] mx-auto px-6 py-8 w-full">
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-[10px] bg-[#338edc] flex items-center justify-center text-white">
            <Calculator className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-[22px] font-bold text-[#1A1A2E]">{t('calcPublic.title')}</h1>
            <p className="text-[13px] text-[#6B7280]">{t('calcPublic.subtitle')}</p>
          </div>
        </div>

        <CalculatorCore
          mode={internal ? 'internal' : 'public'}
          onResultChange={handleResultChange}
          renderActions={renderActions}
        />

        {/* ── Lead Form (public only) ── */}
        {!internal && (
          <div ref={formRef} className="mt-10 max-w-[640px] mx-auto">
            <div className="border border-[var(--border-default,#E5E7EB)] rounded-[16px] bg-white p-8">
              {submitted ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-[18px] font-bold text-[#1A1A2E] mb-2">{t('calcPublic.thankYouTitle')}</h3>
                  <p className="text-[14px] text-[#6B7280]">{t('calcPublic.thankYou')}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <Send className="w-5 h-5 text-[#338edc]" />
                    <h2 className="text-[18px] font-bold text-[#1A1A2E]">{t('calcPublic.requestOffer')}</h2>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">{t('calcPublic.company')} *</label>
                        <input
                          type="text" required value={form.company}
                          onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] text-[14px] text-[#1A1A2E] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20 outline-none"
                          placeholder={t('calcPublic.companyPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">{t('calcPublic.name')} *</label>
                        <input
                          type="text" required value={form.name}
                          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] text-[14px] text-[#1A1A2E] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20 outline-none"
                          placeholder={t('calcPublic.namePlaceholder')}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">{t('calcPublic.email')} *</label>
                        <input
                          type="email" required value={form.email}
                          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] text-[14px] text-[#1A1A2E] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20 outline-none"
                          placeholder={t('calcPublic.emailPlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">{t('calcPublic.phone')}</label>
                        <input
                          type="tel" value={form.phone}
                          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-[8px] border border-[var(--border-default,#E5E7EB)] text-[14px] text-[#1A1A2E] focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/20 outline-none"
                          placeholder={t('calcPublic.phonePlaceholder')}
                        />
                      </div>
                    </div>
                    {formError && (
                      <p className="text-[13px] text-red-600">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-[10px] bg-[#338edc] text-white text-[14px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                      {submitting ? '...' : t('calcPublic.submit')}
                    </button>
                    <p className="text-[11px] text-[#9CA3AF] text-center">
                      {t('calcPublic.footer')}
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-[var(--border-default,#E5E7EB)]">
        <div className="max-w-[1360px] mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-[12px] text-[#9CA3AF]">
            © 2026 We Ship Fulfillment GmbH
          </div>
          <a href="https://www.weship.eu" target="_blank" rel="noopener noreferrer" className="text-[12px] text-[#9CA3AF] hover:text-[#338edc] transition-colors">
            www.weship.eu
          </a>
        </div>
      </footer>
    </div>
  );
}
